import type { PcDatalayer } from "@pamperedchef/datalayer";

interface CommonAnalyticsInit {
	initAnalytics: (initDatalayer?: Record<string, unknown>) => void;
	addHeadScripts: (initDatalayer?: Record<string, unknown>) => void;
	addBodyScripts: () => void;
}

interface CommonAnalyticsEvents {
	pushStepEvent?: (eventName: string, step: string, stepName: string) => void;
}

interface CommonAnalyticsGlobal {
	init: CommonAnalyticsInit;
	events: CommonAnalyticsEvents & Record<string, unknown>;
	dataLayer: PcDatalayer;
}

declare global {
	interface Window {
		PamperedChefCommonAnalytics?: CommonAnalyticsGlobal;
	}
}

/**
 * Safely access the common-analytics global.
 * Returns `undefined` during SSR or before the script has loaded.
 */
export function getCommonAnalytics(): CommonAnalyticsGlobal | undefined {
	if (typeof window === "undefined") return undefined;
	return window.PamperedChefCommonAnalytics;
}

/**
 * Get the datalayer implementation for use with the @pamperedchef/datalayer SDK.
 */
export function getDataLayer(): PcDatalayer | undefined {
	return getCommonAnalytics()?.dataLayer;
}

/**
 * Check whether the common-analytics script has loaded and is available.
 */
export function isAnalyticsReady(): boolean {
	return getCommonAnalytics()?.dataLayer !== undefined;
}

/**
 * Run a callback once common-analytics is ready.
 * Invokes immediately when already loaded; otherwise polls until ready.
 */
export function onAnalyticsReady(callback: () => void): () => void {
	if (typeof window === "undefined") {
		return () => {};
	}

	if (isAnalyticsReady()) {
		callback();
		return () => {};
	}

	const intervalId = window.setInterval(() => {
		if (!isAnalyticsReady()) return;
		window.clearInterval(intervalId);
		callback();
	}, 50);

	return () => window.clearInterval(intervalId);
}
